import { ref, unref, computed, watch } from "vue";
import { findAncestor } from "../utils/dom";
import { deepMap } from "../utils/objects";
import { uuidv4, toUpperFirst } from "../utils/string";
import IMask from "imask";

const VALIDATION_ATTRIBUTES = ["required", "requiredIf", "min", "max", "minlength", "maxlength", "pattern"];
const VALIDATION_MESSAGE_REGEX = /-message(?:-([a-z]{2}(?:_[A-Z]{2})?))?/; // matches, -message, -message-en, -message-en_US

const VALIDATION_TESTS = {
  text: { name: "text", fn: () => true, priority: 0 },
  required: {
    name: "required",
    fn: function (val) {
      return val !== "" && val !== undefined && val !== null && (!Array.isArray(val) || (Array.isArray(val) && val.length));
    },
    priority: 99,
    halt: true,
  },
  requiredIf: {
    name: "requiredIf",
    fn: function (val, conditionFn) {
      const shouldBeRequired = typeof conditionFn === "function" ? conditionFn() : false;
      if (!shouldBeRequired) return true;
      return VALIDATION_TESTS.required.fn.call(this, val);
    },
    priority: 98,
    halt: true,
  },
  email: {
    name: "email",
    fn: (val) => !val || /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(val),
  },
  number: { name: "number", fn: (val) => !val || !isNaN(parseFloat(val)), priority: 2 },
  integer: { name: "integer", fn: (val) => !val || /^\d+$/.test(val) },
  minlength: { name: "minlength", fn: (val, length) => !val || val.length >= parseInt(length) },
  maxlength: { name: "maxlength", fn: (val, length) => !val || val.length <= parseInt(length) },
  min: {
    name: "min",
    fn: function (val, limit) {
      return !val || (this.type === "checkbox" ? groupedElemCount(this) >= parseInt(limit) : parseFloat(val) >= parseFloat(limit));
    },
  },
  max: {
    name: "max",
    fn: function (val, limit) {
      return !val || (this.type === "checkbox" ? groupedElemCount(this) <= parseInt(limit) : parseFloat(val) <= parseFloat(limit));
    },
  },
  hexcolor: {
    name: "hexcolor",
    fn: (val) => !val || /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(val),
    priority: 1,
  },    
  pattern: {
    name: "pattern",
    fn: (val, pattern) => {
      let m = pattern.match(new RegExp("^/(.*?)/([gimy]*)$"));
      return !val || new RegExp(m[1], m[2]).test(val);
    },
  },
  equals: {
    name: "equals",
    fn: (val, otherFieldSelector) => {
      let other = document.querySelector(otherFieldSelector);
      return other && ((!val && !other.value) || other.value === val);
    },
  },
};

const VALIDATION_MESSAGES = {
  en: {
    required: "This field is required",
    requiredIf: "This field is required",
    email: "This field requires a valid e-mail address",
    number: "This field requires a number",
    integer: "This field requires an integer value",
    url: "This field requires a valid website URL",
    tel: "This field requires a valid telephone number",
    hexcolor: "This field requires a valid hex color code (e.g., #123abc)",
    maxlength: "This fields length must be < ${1}",
    minlength: "This fields length must be > ${1}",
    min: "Minimum value for this field is ${1}",
    max: "Maximum value for this field is ${1}",
    pattern: "Please match the requested format",
    equals: "The two fields do not match",
    default: "Please enter a correct value",
  },
  fr: {
    required: "Ce champ est requis",
    requiredIf: "Ce champ est requis.",
    email: "L'email n'est pas valide.",
    number: "This field requires a number",
    integer: "This field requires an integer value",
    url: "This field requires a valid website URL",
    tel: "This field requires a valid telephone number",
    hexcolor: "Ce champ doit contenir une couleur hexadécimale valide (ex : #123abc)",
    maxlength: "This fields length must be < ${1}",
    minlength: "This fields length must be > ${1}",
    min: "La valeur minimale pour ce champs est ${1}",
    max: "La valeur maximale pour ce champs est ${1}",
    pattern: "Please match the requested format",
    equals: "The two fields do not match",
    default: "Please enter a correct value",
  },
};

function tmpl() {
  return this.replace(/\${([^{}]*)}/g, (a, b) => arguments[b]);
}

function getInputValue(input) {
  if (input.type === "radio" || input.type === "checkbox") {
    console.log(input._validation);
    const checkedElements = Array.from(input._validation.form.node.querySelectorAll('input[name="' + input.getAttribute("name") + '"]:checked')).map((el) => el.value);
    return checkedElements.length ? checkedElements : null;
  }
  // Check for our FileUpload component. Fo them, we need to check all files Name[] @see FileUploadTagHelper
  if (input.type === "file" && input.dataset.name) {
    let form = input;
    while (form.tagName != "FORM" && form.parentNode) {
      form = form.parentNode;
    }
    const values = Array.from((form ?? document).querySelectorAll(`input[name="${input.dataset.name}[]"]`));
    return values.join(",");
  }
  return input.value;
}

function _getDataMethod(node, name) {
  name = `form${toUpperFirst(name)}`;
  return name in node.dataset && node.dataset[name] in window ? window[node.dataset[name]] : () => void 0;
}

const _initForm = (form) => {
  if (form._validation) return;

  form.setAttribute("novalidate", "true");

  const live = !form.dataset.formLive;
  const submitButton = form.querySelector(form.dataset.submitButton || "button");
  const submitDelay = form.dataset.submitDelay || 1000;

  if (submitButton && submitButton.type == "submit") {
    console.warn('form data-submit-button type MUST NOT be "submit".');
  }

  const self = {
    node: form,
    dirty: true,
    errors: {},
    fields: [],
    masks: [],
    live: !(live === false),
    reset: function () {
      self.dirty = true;
      self.errors = {};
      Array.from(self.fields).forEach((field) => {
        const input = field.input;
        if (input.type === "radio" || input.type === "checkbox") input.checked = false;
        else input.value = "";

        _showSuccess(field);
      });
    },
    onPost: _getDataMethod(form, "onpost"),
    onBeforePost: _getDataMethod(form, "onbeforepost"), 
    createPromise: _getDataMethod(form, "createPromise"),
    updatePromise: _getDataMethod(form, "updatePromise"),
    deletePromise: _getDataMethod(form, "deletePromise"),
    validate: function (input, silent) {
      let firstErrorInput = null;
      let fullFormValidation = true;

      silent = (input && silent === true) || input === true;

      let fields = self.fields;
      if (input !== true && input !== false && input !== undefined) {
        if (input instanceof HTMLElement) {
          fields = [input._validation];
        } else if (input instanceof NodeList || input instanceof (window.$ || Array) || input instanceof Array) {
          fields = Array.from(input).map((el) => el._validation);
        }
        fullFormValidation = false;
      }

      if (fullFormValidation) {
        self.dirty = false; // remove dirty state after the first global form validation
      }

      let valid = true;
      fields.forEach((field) => {
        if (_validateField(field)) {
          !silent && _showSuccess(field);
        } else {
          if (!firstErrorInput) {
            firstErrorInput = field;
          }
          valid = false;
          !silent && _showError(field);
        }
      });

      if (fullFormValidation) {
        // Focus the first invalid input
        if (firstErrorInput) {
          firstErrorInput.input.focus();
        }

        if (valid) {
          if (self.onBeforePost() === false) {
            return false;
          }
          self.onPost();
        }
      }
      return valid;
    },
  };

  const inputs = Array.from(form.querySelectorAll("input:not([type^=hidden]):not([type^=submit]):not([type^=button]), select, textarea"));
  inputs.forEach((input) => {
    let fns = [];
    let params = {};
    let messages = {};

    [].forEach.call(input.attributes, function (attr) {
      if (/^data-form-/.test(attr.name)) {
        let name = attr.name.substr("data-form-".length);

        let messageMatch = name.match(VALIDATION_MESSAGE_REGEX);
        if (messageMatch !== null) {
          let locale = messageMatch[1] === undefined ? "fr" : messageMatch[1];
          if (!messages.hasOwnProperty(locale)) {
            messages[locale] = {};
          }
          messages[locale][name.slice(0, name.length - messageMatch[0].length)] = attr.value;
          return;
        }
        if (name === "type") name = attr.value;
        _addValidatorToField(fns, params, name, attr.value);
      } else if (~VALIDATION_ATTRIBUTES.indexOf(attr.name)) {
        _addValidatorToField(fns, params, attr.name, attr.value);
      } else if (attr.name === "type") {
        _addValidatorToField(fns, params, attr.value);
      }
    });
    fns.sort((a, b) => (b.priority || 1) - (a.priority || 1));

    self.live &&
      input.addEventListener(
        !~["radio", "checkbox"].indexOf(input.getAttribute("type")) ? "input" : "change",
        function (e) {
          if (!self.dirty) {
            self.validate(e.target);
          }
        }.bind(self),
      );

    /// Pressing return in an Input will call the submitButton click thats submit the form
    if (input.tagName != "TEXTAREA") {
      input.addEventListener("keydown", function (e) {
        if (e.keyCode === 13) {
          submitButton && submitButton.click();
          e.preventDefault();
        }
      });
    }

    // INPUT Masks
    // @see https://imask.js.org/guide.html
    // Note: when form is posted, all input with mask will be replaced with the unmasker value
    if (input.tagName == "INPUT") {
      const inputMask = input.getAttribute("mask");
      if (inputMask) {
        const mask = IMask(input, { mask: inputMask });
        self.masks.push({ mask, input });
        input._mask = mask;
      }
    }

    const togglePassword = input.parentNode?.querySelector(".a-input-password-toggle");
    if (togglePassword) {
      const onTogglePassword = () => {
        if (input.type === "password") {
          input.setAttribute("type", "text");
          togglePassword.innerHTML = "Masquer";
        } else {
          input.setAttribute("type", "password");
          togglePassword.innerHTML = "Afficher";
        }
      };
      ["click", "touch", "keypress"].forEach((name) => togglePassword.addEventListener(name, onTogglePassword))
    }

    input._validation = {
      input,
      validators: fns,
      params,
      messages,
      messageNode: _getOrcreateMessageNode(input),
      fieldNode: findAncestor(input, ".a-field"),
      form: self,
    };
    self.fields.push(input._validation);
  });

  form._validation = self;

  if (submitButton) {
    submitButton.addEventListener("click", () => {
      if (self.validate()) {
        if (form.onsubmit && form.onsubmit() === false) {
          return;
        }
        submitButton.classList.add("a-button-loading");

        // replace input with mask values
        self.masks.forEach(({ mask, input }) => {
          input.value = mask.unmaskedValue;
          mask.destroy();
        });

        // Form protection with Google recaptcha v3 ? (@see RecaptchaTagHelper)
        const recaptchaNode = form.querySelector("input[data-recaptcha-key]");
        if (recaptchaNode) {
          try {
            console.info("Generate recaptcha token.");
            window.grecaptcha.ready(function () {
              window.grecaptcha.execute(recaptchaNode.dataset.recaptchaKey, { action: recaptchaNode.dataset.recaptchaAction }).then(function (token) {
                recaptchaNode.value = token;
                console.info("Recaptcha token generate.");
                setTimeout(() => form.submit(), submitDelay);
              });
            });
          } catch {
            console.warn("Recaptcha token generation failed.");
            // if js error => post form to let the server return the error 
            setTimeout(() => form.submit(), submitDelay);
          }
        } else {
          setTimeout(() => form.submit(), submitDelay);
        }
      }
    });
  }
};

function _getOrcreateMessageNode(input) {
  const parent = input.closest(".a-field");

  if (!parent) {
    console.warn("input has no .field parent.", input);
    return null;
  }

  let messageNode = parent.querySelector(".a-field-message");
  if (!messageNode) {
    messageNode = document.createElement("div");
    parent.appendChild(messageNode);
    messageNode.className = "a-field-message";
    messageNode.innerHTML = '<span class="ico ico-alert"></span><span class="a-field-message-content"></span>';
  }

  return messageNode;
}

function _addValidatorToField(fns, params, name, value) {
  const validator = VALIDATION_TESTS[name];
  if (validator && !fns.find((f) => f.name === name)) {
    fns.push(validator);

    if (typeof value === "function") {
      params[name] = [null, value]; // valeur dynamique (e.g. requiredIf)
    } else if (typeof value === "string") {
      let valueParams = name === "pattern" ? [value] : value.split(",");
      valueParams.unshift(null); // placeholder pour la valeur à valider
      params[name] = valueParams;
    } else {
      // fallback pour types inattendus (ex: boolean true/false)
      params[name] = [null, value];
    }
  }
}

function _validateField(field) {
  let errors = [];
  let currentLocale = "fr";
  let valid = true;
  for (let i = 0; field.validators[i]; i++) {
    let validator = field.validators[i];
    const value = getInputValue(field.input);
    let params = field.params[validator.name] ? field.params[validator.name] : [];
    params[0] = value;

    if (!validator.fn.apply(value, params)) {
      valid = false;
      if (typeof validator.msg === "function") {
        errors.push(validator.msg(value, params));
      } else if (typeof validator.msg === "string") {
        errors.push(tmpl.apply(validator.msg, params));
      } else if (validator.msg === Object(validator.msg) && validator.msg[currentLocale]) {
        // typeof generates unnecessary babel code
        errors.push(tmpl.apply(validator.msg[currentLocale], params));
      } else if (field.messages[currentLocale] && field.messages[currentLocale][validator.name]) {
        errors.push(tmpl.apply(field.messages[currentLocale][validator.name], params));
      } else if (VALIDATION_MESSAGES[currentLocale] && VALIDATION_MESSAGES[currentLocale][validator.name]) {
        errors.push(tmpl.apply(VALIDATION_MESSAGES[currentLocale][validator.name], params));
      } else {
        errors.push(tmpl.apply(VALIDATION_MESSAGES[currentLocale].default, params));
      }

      if (validator.halt === true) {
        break;
      }
    }
  }
  field.errors = errors;
  return valid;
}

function _showSuccess(field) {
  setTimeout(() => {
    const classList = field.messageNode?.classList;
    if (classList) {
      classList.remove("visible");
      field.fieldNode && field.fieldNode.classList.remove("a-invalid-field");
    }
  }, 15); // Timeout let the fade in/out effect works
}

function _showError(field) {
  setTimeout(() => {
    field.fieldNode && field.fieldNode.classList.add("a-invalid-field");
    field.messageNode.querySelector(".a-field-message-content").innerHTML = field.errors.join("<br/>");

    const classList = field.messageNode?.classList;
    if (classList) {
      classList.add("invalid");
      classList.add("visible");
    }
  }, 15); // Timeout let the fade in/out effect works
}

/*
 *
 * VueJs adaptater
 *
 */
export function initRazorFormValidation(win) {
  const forms = Array.from(win.document.querySelectorAll("form"));
  forms.forEach(_initForm);
}

const getRuleFromPath = (rulesObj, pathStr) => {
    return pathStr.split(".").reduce((obj, key) => obj?.[key], rulesObj);
};

function checkState(state, rules, $errors) {
  let count = 0;

  const todoMissingCheckStates = [];

  $errors.value = deepMap(state, (prop, value, path) => {
    const $errors = [];

    const fieldRules = getRuleFromPath(rules, path);
    if (fieldRules) {
        let validators = [];
        let params = {};

        for (const ruleName in fieldRules) {
          const ruleValue = fieldRules[ruleName];
          _addValidatorToField(validators, params, ruleName, ruleValue);
        }
    
        validators.sort((a, b) => (b.priority || 1) - (a.priority || 1));
    
        for (let i = 0; i < validators.length; i++) {
            const validator = validators[i];
            params = params[validator.name] ? params[validator.name] : [];
            params[0] = value;
    
            if (!validator.fn.apply(null, params)) {
                count++;
                $errors.push({
                    $message: VALIDATION_MESSAGES["fr"][validator.name],
                    $property: path,
                    $validator: validator.name,
                    $uid: uuidv4(),
                });
    
                if (validator.halt === true) break;
            }
        }
    }
    return { 
      $errors,
      rules: rules[prop] ?? null,
    };
  });
  
  if (todoMissingCheckStates.length) {
    console.log("TODO: Not Implemented Children path for", todoMissingCheckStates);
  }
  return count;
}

export function useValidationForm(rules, state) {
  const initErrors = () => deepMap(state, (prop) => ({ 
    $errors: [],
    rules: prop in rules ? rules[prop] : null,
  }));

  let $dirty = ref(true);
  let $invalid = ref(false);
  const $errors = ref(initErrors());

  watch(
    () => state,
    (newState) => {
      if (!$dirty.value) {
        $invalid.value = checkState(newState, rules, $errors) > 0;
      }
    },
    { deep: true },
  );

  const $touch = () => {
    $dirty.value = false;
    $invalid.value = checkState(state, rules, $errors) > 0;
  };

  const $reset = () => {
    $dirty.value = true;
    $errors.value = initErrors();
    $invalid.value = false;
  };

  return computed(() => {
    return {
      ...unref($errors),
      $dirty: unref($dirty),
      $invalid: unref($invalid),
      $errors,
      $touch,
      $reset,
    };
  });
}
